import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  updateProfile,
  type User as FirebaseUser,
} from 'firebase/auth';
import { create } from 'zustand';
import type { User } from '@liveforge/shared';
import { auth } from '../lib/firebase';
import { errorMessage } from '../lib/api';
import { login } from '../lib/resources';

/**
 * `loading` means Firebase has not yet replayed the persisted session. Routing
 * decisions taken during that window would bounce a signed-in user to /login,
 * so it is kept as an explicit third state rather than folded into
 * "unauthenticated".
 */
export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthState {
  status: AuthStatus;
  firebaseUser: FirebaseUser | null;
  /** The LiveForge user row, returned by `POST /api/auth/login`. */
  user: User | null;
  /** Set when the backend user sync failed even though Firebase sign-in worked. */
  syncError: string | null;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string, displayName: string) => Promise<void>;
  signOut: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  status: 'loading',
  firebaseUser: null,
  user: null,
  syncError: null,

  signInWithGoogle: async () => {
    await signInWithPopup(auth, new GoogleAuthProvider());
  },

  signInWithEmail: async (email, password) => {
    await signInWithEmailAndPassword(auth, email, password);
  },

  signUpWithEmail: async (email, password, displayName) => {
    const credential = await createUserWithEmailAndPassword(auth, email, password);
    const trimmed = displayName.trim();
    if (trimmed) {
      await updateProfile(credential.user, { displayName: trimmed });
    }
  },

  signOut: async () => {
    await firebaseSignOut(auth);
    set({ user: null, firebaseUser: null, status: 'unauthenticated', syncError: null });
  },
}));

/** Uid whose backend sync already ran, so a re-emitted state does not repeat it. */
let syncedUid: string | null = null;
let listenerAttached = false;

/**
 * Subscribes to Firebase auth state exactly once, and mirrors it into the
 * store. Called from the application entry point.
 */
export function initAuthListener(): void {
  if (listenerAttached) {
    return;
  }
  listenerAttached = true;

  onAuthStateChanged(auth, (firebaseUser) => {
    if (!firebaseUser) {
      syncedUid = null;
      useAuthStore.setState({
        status: 'unauthenticated',
        firebaseUser: null,
        user: null,
        syncError: null,
      });
      return;
    }

    useAuthStore.setState({ status: 'authenticated', firebaseUser });

    if (syncedUid === firebaseUser.uid) {
      return;
    }
    syncedUid = firebaseUser.uid;

    login()
      .then((user) => {
        useAuthStore.setState({ user, syncError: null });
      })
      .catch((error: unknown) => {
        // Sign-in itself succeeded; only the local user row is missing. Surface
        // it instead of pretending the session is broken.
        syncedUid = null;
        useAuthStore.setState({ syncError: errorMessage(error) });
      });
  });
}

/** Human-readable text for the Firebase auth error codes the UI can provoke. */
export function authErrorMessage(error: unknown): string {
  const code =
    error && typeof error === 'object' && 'code' in error
      ? String((error as { code: unknown }).code)
      : '';
  switch (code) {
    case 'auth/invalid-email':
      return 'That email address is not valid.';
    case 'auth/missing-password':
      return 'Enter your password.';
    case 'auth/weak-password':
      return 'Choose a password of at least 6 characters.';
    case 'auth/email-already-in-use':
      return 'An account already exists for this email address.';
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'Incorrect email or password.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Try again in a few minutes.';
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return 'The sign-in window was closed before finishing.';
    case 'auth/popup-blocked':
      return 'The browser blocked the sign-in window. Allow pop-ups and try again.';
    case 'auth/operation-not-allowed':
      return 'This sign-in method is disabled in the Firebase project.';
    default:
      return errorMessage(error);
  }
}
